import { useStripe } from '@stripe/react-stripe-js'
import addDays from 'date-fns/addDays'

import { isValidPhoneNumber } from 'react-phone-number-input'

import { emailPatternMatch, getAmountDue } from '../../../utilities/helpers'
import {
  getTerminalPaymentErrorMessage,
  isTerminalPaymentCanceledError,
} from '../../../utilities/terminalPayment'

import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'

import { Spinner } from '../../../components/LoadingContainer/Spinner'
import { BackButton } from '../../../components/BackButton'
import { PricingV2 } from '../PricingV2'
import { AssignToV2 } from '../AssignToV2'
import { UserLookup } from './UserLookup'

/** Matches AssignModalFormV2: these methods bake custom fees into pricingObject totals. */
const ASSIGN_CARD_LIKE_METHODS = new Set(['CARD', 'INVOICE', 'POS'])
const isAssignCardLikePaymentMethod = (method) =>
  ASSIGN_CARD_LIKE_METHODS.has(String(method || '').toUpperCase())

export default function ModalForm({
  selectedTickets,
  price,
  setPrice,
  priceTiers,
  paymentMethod,
  setPaymentMethod,
  pricingObject,
  isComplimentary,
  assignTo,
  handleAssignToChange,
  isValidEmail,
  setIsValidEmail,
  isValidNumber,
  setIsValidNumber,
  ticketType,
  buyerTypes,
  selectedBuyerType,
  setSelectedBuyerType,
  compLabels,
  selectedCompLabel,
  setSelectedCompLabel,
  clientSecret,
  initialClientSecret,
  setCard,
  setStripe,
  userType,
  setUserType,
  setInitialExistingUser,
  existingUser,
  setExistingUser,
  installmentMonths,
  setInstallmentMonths,
  paymentPlans,
  setPaymentDate,
  paymentSchedule,
  setPaymentSchedule,
  loadExistingUsers,
  setIsCardValid,
  savedCards,
  isLoadingCards,
  selectedCard,
  setSelectedCard,
  selectedCardOpt,
  setSelectedCardOpt,
  step,
  alert,
  closeModal,
  isValid,
  isSaving,
  setIsSaving,
  getText,
  isPackage,
  handleGoBack,
  setStep,
  createOrder,
  card,
  openError,
  showPaymentSuccess,
  showAssignmentSuccess,
  onInvoiceSuccess,
  openMarkAsComplete,
  openDelete,
  processTerminalPayment,
  terminalPaymentTimedOutRef,
  createPaymentPlanOrder,
  chargePaymentMethod,
  ensureSeatHold,
  eventPackage,
  dueDateOpt,
  setDueDateOpt,
  customDueDate,
  setCustomDueDate,
  showRemoveSeat,
  sourceOrderTotal,
  onRemoveSeat,
  accessPassQuantity,
  onAccessPassQuantityChange,
  customFeesList = [],
  customFeeRows = [],
  addCustomFeeRow,
  removeCustomFeeRow,
  setCustomFeeRowFeeId,
  totalCustomFeeAmount = 0,
  customFeeSummaryLines = [],
  organizationUUIDForAssignment,
  donationEvaluation = null,
  selectedDonationRuleId,
  setSelectedDonationRuleId,
  configuredDonations = [],
  onApplyDonation,
  donationAmount = 0,
  setDonationAmount,
  donationExternalPending = false,
  setDonationExternalPending,
  onViewDonation,
  donationChargeAmount = 0,
  supplementalFeeOnly = false,
}) {
  const stripe = useStripe()
  const displayAmountPaymentMethod =
    paymentMethod === 'RESERVED' ? 'CASH' : paymentMethod
  // Card/invoice/POS totals already include custom fees via effectivePricingObject; do not add again.
  const feeExtra =
    paymentMethod !== 'COMP' &&
    paymentMethod !== 'RESERVED' &&
    !isAssignCardLikePaymentMethod(paymentMethod)
      ? totalCustomFeeAmount
      : 0
  const sourceOrderAmount = Number(sourceOrderTotal)
  const hasSourceOrderAmount =
    showRemoveSeat &&
    Number.isFinite(sourceOrderAmount) &&
    sourceOrderAmount > 0

  async function handleCardPayment(hasPaymentPlan) {
    setIsSaving(true)

    try {
      // Lock the seats before the card is charged. If they were taken while
      // the operator was pricing the sale, fail here rather than collecting
      // money we can't turn into tickets.
      if (ensureSeatHold) {
        try {
          await ensureSeatHold()
        } catch (holdErr) {
          setIsSaving(false)
          const holdMessage = String(
            holdErr?.response?.data?.error?.message ||
              holdErr?.response?.data?.message ||
              holdErr?.message ||
              ''
          )
          openError(
            'Seats no longer available',
            holdMessage ||
              'Those seats were just taken by another buyer. No payment was processed. Please reselect and try again.'
          )
          return
        }
      }

      let paymentIntentId

      if (!hasPaymentPlan && selectedCard) {
        const pmid = await chargePaymentMethod()
        if (pmid?.error) throw new Error(pmid.error.message)
        paymentIntentId = pmid?.data.id
      } else {
        const payload = await stripe.confirmCardPayment(clientSecret, {
          payment_method: selectedCard ? selectedCard : { card },
        })
        if (payload?.error) throw new Error(payload.error.message)
        paymentIntentId = payload?.paymentIntent?.id
      }

      hasPaymentPlan
        ? createPaymentPlanOrder(paymentIntentId, showPaymentSuccess)
        : createOrder(paymentIntentId, showPaymentSuccess)
    } catch (err) {
      setIsSaving(false)
      openError(
        'Payment failed!',
        `Payment could not be processed. ${err.message} Please try again.`
      )
    }
  }

  const handleSave = async (e) => {
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0

    e.preventDefault()

    const isValidEmail = (email) => {
      const trimmed = String(email ?? '').trim()
      if (!trimmed) return false
      return emailPatternMatch(trimmed)
    }

    const isValidNumber = (number) => {
      // Since not required -> empty is valid
      if (!number) {
        return true
      }

      return isValidPhoneNumber(number)
    }
    if (step === 0 && isValid) {
      // Existing user selected on lookup — advance to buyer info with fields filled
      setUserType('existing-user')
      setStep(1)
      return
    }
    if (step === 1 && isValid) {
      // Keep existing-user when a looked-up account is in play; only mark new-user
      // for the manual "Continue with new account" path.
      if (!existingUser) {
        setUserType('new-user')
      }
      setStep(2)
      return
    }
    if (step === 2) {
      if (!isValidEmail(assignTo.email)) {
        openError(
          'Invalid email',
          'Please enter a valid email address for the buyer.'
        )
        return
      }
      if (!isValidNumber(assignTo.phoneNumber)) {
        openError(
          'Invalid phone number',
          'Please enter a valid phone number or clear the field.'
        )
        return
      }

      switch (paymentMethod) {
        case 'INVOICE':
          setIsSaving(true)

          let dueDate
          if (dueDateOpt === 'custom') {
            dueDate = customDueDate
          } else {
            // tomorrow, 7, 14 etc.
            if (dueDateOpt > 0) {
              dueDate = addDays(new Date(), dueDateOpt)
            } else {
              // today
              dueDate = new Date()
            }
          }
          if (!hasPaymentPlan) {
            createOrder('', onInvoiceSuccess, dueDate?.toISOString())
          }
          break

        case 'POS':
          if (hasPaymentPlan) break

          setIsSaving(true)

          try {
            const terminalPayment = await processTerminalPayment()
            const paymentIntentId = terminalPayment?.paymentIntent?.id

            if (!paymentIntentId) {
              throw new Error(
                'Terminal payment completed without a payment intent id.'
              )
            }

            console.log('Payment succeeded', terminalPayment)
            await createOrder(paymentIntentId, showPaymentSuccess)
          } catch (error) {
            setIsSaving(false)
            const title = isTerminalPaymentCanceledError(error)
              ? 'Payment canceled'
              : 'Payment failed!'
            openError(
              title,
              getTerminalPaymentErrorMessage(error, {
                timedOut: terminalPaymentTimedOutRef?.current,
              })
            )
            console.error('Terminal payment failed:', error)
          }
          break

        case 'CARD':
          handleCardPayment(hasPaymentPlan)
          break

        default:
          const allowed = ['COMP', 'RESERVED']
          if (
            allowed.includes(paymentMethod) ||
            getAmountDue(
              pricingObject,
              paymentMethod,
              hasPaymentPlan,
              paymentPlans
            ) +
              feeExtra ===
              0
          ) {
            setIsSaving(true)
            createOrder('', showAssignmentSuccess)
            return
          }
          openMarkAsComplete(paymentMethod)
          break
      }
    }
  }

  const getHeading = () => {
    // user lookup
    if (step === 0) {
      return {
        title: 'User lookup',
        desc: 'Look up a user to get their details for faster payment processing if they have a blocktickets account. If not, you can enter the information manually later.',
      }
      // new user
    } else if (step === 1) {
      return {
        title: 'Add buyer information',
        desc: 'This information will be stored in the system.',
      }
      // assign
    } else {
      return {
        title: supplementalFeeOnly
          ? 'Add custom fee'
          : `Assign ${isPackage ? 'package' : 'tickets'}`,
        desc: supplementalFeeOnly
          ? 'Choose a custom fee and collect the additional payment.'
          : "Choose the options that meet the user's requirements.",
      }
    }
  }

  const heading = getHeading()

  return (
    <>
      <div className="modal-body-heading">
        <h1 className="modal-body-heading-title">{heading?.title}</h1>
        <p className="mt-0 normal">{heading?.desc}</p>
      </div>
      <Form onSubmit={handleSave} className="d-flex-column flex-grow-1">
        {step === 0 ? (
          <UserLookup
            existingUser={existingUser}
            setExistingUser={setExistingUser}
            loadExistingUsers={loadExistingUsers}
            setInitialExistingUser={setInitialExistingUser}
            setStep={setStep}
            setUserType={setUserType}
          />
        ) : (
          <>
            {step === 1 ? (
              <AssignToV2
                assignTo={assignTo}
                onAssignToChange={handleAssignToChange}
                isValidEmail={isValidEmail}
                setIsValidEmail={setIsValidEmail}
                isValidNumber={isValidNumber}
                setIsValidNumber={setIsValidNumber}
              />
            ) : (
              <PricingV2
                assignTo={assignTo}
                existingUser={existingUser}
                amountToPay={
                  (hasSourceOrderAmount
                    ? sourceOrderAmount + feeExtra
                    : getAmountDue(
                        pricingObject,
                        displayAmountPaymentMethod,
                        Object.keys(paymentPlans).length > 0,
                        paymentPlans
                      ) + feeExtra) + donationChargeAmount
                }
                totalAmount={
                  hasSourceOrderAmount
                    ? sourceOrderAmount + feeExtra
                    : getAmountDue(
                        pricingObject,
                        displayAmountPaymentMethod,
                        false,
                        {}
                      ) + feeExtra
                }
                selectedTickets={selectedTickets}
                showRemoveSeat={showRemoveSeat}
                onRemoveSeat={onRemoveSeat}
                accessPassQuantity={accessPassQuantity}
                onAccessPassQuantityChange={onAccessPassQuantityChange}
                isPackage={isPackage}
                ticketType={ticketType}
                price={price}
                setPrice={setPrice}
                priceTiers={priceTiers}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                allowEditPrice={!supplementalFeeOnly}
                supplementalFeeOnly={supplementalFeeOnly}
                pricingObject={pricingObject}
                paymentSchedule={paymentSchedule}
                setPaymentSchedule={setPaymentSchedule}
                installmentMonths={installmentMonths}
                setInstallmentMonths={setInstallmentMonths}
                paymentPlans={paymentPlans}
                setPaymentDate={setPaymentDate}
                setIsCardValid={setIsCardValid}
                savedCards={savedCards}
                isLoadingCards={isLoadingCards}
                selectedCard={selectedCard}
                setSelectedCard={setSelectedCard}
                openDelete={openDelete}
                selectedCardOpt={selectedCardOpt}
                setSelectedCardOpt={setSelectedCardOpt}
                eventPackage={eventPackage}
                dueDateOpt={dueDateOpt}
                setDueDateOpt={setDueDateOpt}
                customDueDate={customDueDate}
                setCustomDueDate={setCustomDueDate}
                buyerTypes={buyerTypes}
                selectedBuyerType={selectedBuyerType}
                compLabels={compLabels}
                selectedCompLabel={selectedCompLabel}
                setSelectedBuyerType={setSelectedBuyerType}
                setSelectedCompLabel={setSelectedCompLabel}
                clientSecret={clientSecret}
                initialClientSecret={initialClientSecret}
                setCard={setCard}
                setStripe={setStripe}
                isReserving={showRemoveSeat}
                customFeesList={customFeesList}
                customFeeRows={customFeeRows}
                addCustomFeeRow={addCustomFeeRow}
                removeCustomFeeRow={removeCustomFeeRow}
                setCustomFeeRowFeeId={setCustomFeeRowFeeId}
                totalCustomFeeAmount={totalCustomFeeAmount}
                customFeeSummaryLines={customFeeSummaryLines}
                organizationUUIDForAssignment={organizationUUIDForAssignment}
                donationEvaluation={donationEvaluation}
                selectedDonationRuleId={selectedDonationRuleId}
                setSelectedDonationRuleId={setSelectedDonationRuleId}
                configuredDonations={configuredDonations}
                onApplyDonation={onApplyDonation}
                donationAmount={donationAmount}
                setDonationAmount={setDonationAmount}
                donationExternalPending={donationExternalPending}
                setDonationExternalPending={setDonationExternalPending}
                onViewDonation={onViewDonation}
                donationChargeAmount={donationChargeAmount}
              />
            )}
          </>
        )}
        {alert.show && (
          <Alert variant={alert.variant} className="mt-4">
            <p>{alert.message}</p>
          </Alert>
        )}
        <Stack
          direction="horizontal"
          className={`btn-group-flex ${
            step !== 0 ? 'justify-content-between' : ''
          } `}
        >
          {step !== 0 && <BackButton size="sm" handleGoBack={handleGoBack} />}
          <Stack direction="horizontal">
            <Button variant="default" size="lg" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              size="lg"
              className={`btn-width ${step !== 2 ? 'btn-next btn-width-sm--flex-item' : 'btn-width-xxl--flex-item'}`}
              disabled={!isValid || isSaving}
              type="submit"
            >
              {isSaving ? <Spinner /> : step !== 2 ? 'Next' : getText()}
            </Button>
          </Stack>
        </Stack>
      </Form>
    </>
  )
}
